import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { DeviceHistoryService, TimeInterval } from '../http/device-history-service';
import { DeviceService } from '../http/device-service';
import { DeviceInfo, deviceName, DeviceType } from '../types/device';
import { SensorState, SensorType, sensorTypeStr } from '../types/sensor';
import { LogConfig } from '../types/log-config';

interface BatteryLevel {
    chargeLevel: number;
    isCharging: boolean;
}

interface MemoryInfo {
    total: number;
    used: number;
}

interface DeviceHistory {
    lastTransaction: string;
    deviceType?: number;
    deviceFw?: string;
    sensorType?: number;
    sensorId?: number;
    logConfig?: LogConfig;
    memoryInfo?: MemoryInfo;
    battery?: BatteryLevel;
    rssi?: number;
    sensorState?: number;
    cpuTemp?: number;
}

//=================================================================================================
//=================================================================================================
export default class DeviceHistoryStore {
    private _deviceId: number;
    private _loading = false;
    private _items: DeviceHistory[] = [];

    constructor(deviceId: number) {
        this._deviceId = deviceId;
        makeObservable<DeviceHistoryStore, '_loading' | '_items'>(this, {
            _loading: observable,
            _items: observable,
            loading: computed,
            items: computed,
            deviceType: computed,
            deviceName: computed,
            lastTransaction: computed,
            deviceFw: computed,
            sensorType: computed,
            sensorId: computed,
            sensorName: computed,
            sensorState: computed,
            logConfig: computed,
            load: action,
        });
    }

    get loading(): boolean {
        return this._loading;
    }

    get items(): DeviceHistory[] {
        return this._items;
    }

    //-------------------------------------------------------------------------------------------------
    get deviceId(): number {
        return this._deviceId;
    }

    get deviceType(): DeviceType {
        for (const item of this._items) {
            if (item.deviceType !== undefined) {
                return item.deviceType;
            }
        }
        return DeviceType.Device_Unknown;
    }

    get deviceName(): string {
        return `${deviceName(this.deviceType)}-${this._deviceId}`;
    }

    //-------------------------------------------------------------------------------------------------
    get lastTransaction(): string {
        if (this._items.length) return this._items.at(0)!.lastTransaction;
        return 'never';
    }

    get deviceFw(): string {
        for (const item of this._items) {
            if (item.deviceFw !== undefined) {
                return item.deviceFw;
            }
        }
        return '0.0.0';
    }

    //-------------------------------------------------------------------------------------------------
    get sensorType(): SensorType {
        for (const item of this._items) {
            if (item.sensorType !== undefined) {
                return item.sensorType;
            }
        }
        return SensorType.UnknownSensorType;
    }

    get sensorId(): number | null {
        for (const item of this._items) {
            if (item.sensorId !== undefined) {
                return item.sensorId;
            }
        }
        return null;
    }

    get sensorName(): string {
        const sensorId = this.sensorId;
        if (sensorId === null) return 'Не подключен';
        return `${sensorTypeStr(this.sensorType)}-${sensorId}`;
    }

    get sensorState(): SensorState | null {
        for (const item of this._items) {
            if (item.sensorState !== undefined) {
                return item.sensorState;
            }
        }
        return null;
    }

    //-------------------------------------------------------------------------------------------------
    get logConfig(): LogConfig | null {
        for (const item of this._items) {
            if (item.logConfig !== undefined) {
                return item.logConfig;
            }
        }
        return null;
    }

    //-------------------------------------------------------------------------------------------------
    async load(interval: TimeInterval): Promise<void> {
        runInAction(() => {
            this._loading = true;
        });
        try {
            const answer = await DeviceHistoryService.getHistory(this._deviceId, interval);
            let items = answer.data;
            if (!items.length) {
                //Данных за выбранный интервал нет. Запрашиваем общую информацию об устройстве,
                //чтобы на странице можно было отобразить хотя бы базовые сведения (тип, FW, датчик и т.д.)
                const devInfo = await this.loadDeviceInfo();
                if (devInfo) items = [devInfo];
            }
            runInAction(() => {
                this._items = items;
            });
        } catch (e) {
            throw e;
        } finally {
            runInAction(() => {
                this._loading = false;
            });
        }
    }

    //-------------------------------------------------------------------------------------------------
    /**
     * Запрашивает общую информацию об устройстве и собирает один DeviceHistory.
     * Возвращает null, если общую информацию получить не удалось.
     */
    private static toDeviceHistory(deviceInfo: DeviceInfo): DeviceHistory {
        const info = deviceInfo.info ?? {};
        return {
            lastTransaction: info.lastTransaction ?? 'неизвестно',
            deviceType: info.deviceType,
            deviceFw: info.deviceFw,
            sensorType: info.sensorType,
            sensorId: info.sensorId,
            logConfig: info.logConfig,
            memoryInfo: info.memoryInfo,
            battery: info.battery,
            rssi: info.rssi,
            sensorState: info.sensorState,
            cpuTemp: info.cpuTemp,
        };
    }

    private async loadDeviceInfo(): Promise<DeviceHistory | null> {
        try {
            const res = await DeviceService.byId(this._deviceId);
            return DeviceHistoryStore.toDeviceHistory(res.data as DeviceInfo);
        } catch (e) {
            //Не смогли получить общую информацию — оставляем отсутствие данных как есть
            return null;
        }
    }
}
